import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { GraficoColunas } from "@/components/dashboard/GraficoColunas";
import { SecaoCard } from "@/components/padroes";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { useAtualizacaoNF, STATUS_AGUARDANDO_OCR } from "@/context/useAtualizacaoNF";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import { api } from "@/lib/api";
import { formatBRL, formatCents, formatDate, formatMesRef } from "@/lib/format";
import { mesCurto, porcento } from "@/data/dashboard";
import type { HoleriteHistorico } from "@/data/folha";
import type { NotaFiscalResumo } from "@/data/mock";
import { toast } from "sonner";
import {
  ArrowRight,
  FilePlus2,
  Gift,
  HeartPulse,
  Landmark,
  ReceiptText,
  TrendingUp,
  Trophy,
  type LucideIcon,
} from "lucide-react";

/** GET /dashboard/colaborador/remuneracao — centavos. */
interface Remuneracao {
  periodo: { de: string; ate: string };
  vinculo: "pj" | "clt";
  salarioAtual: number;
  totais: {
    salario: number;
    variaveis: number;
    beneficios: number;
    bonificacao: number;
    saude: number;
    fgts: number;
    total: number;
    alemDoSalario: number;
  };
  mensal: {
    competencia: string;
    salario: number;
    variaveis: number;
    beneficios: number;
    bonificacao: number;
    saude: number;
    fgts: number;
  }[];
  beneficios: { nome: string; total: number; meses: number }[];
  saude: { franquiaCoberta: number; mesesComUso: number; franquiaMensal: number };
  bonificacoes: { competencia: string; valor: number; parcela: number; numeroParcelas: number; adiantamento: boolean }[];
  mesesComFolha: number;
}

/** GET /dashboard/colaborador — NFs do próprio usuário (valores em reais). */
interface ResumoNFs {
  total: number;
  emAnalise: number;
  aprovadas: number;
  recusadas: number;
  valorAprovado: number;
  recentes: NotaFiscalResumo[];
}

/** GET /instrucoes/me — só o que o card de ação do mês usa (centavos). */
interface Instrucoes {
  instrucao: { competencia: string; tomadorNome: string; valor: number; nfStatus: string | null } | null;
}

type Chave = "salario" | "variaveis" | "beneficios" | "bonificacao" | "saude" | "fgts";

const primeiroNome = (nome?: string) => nome?.trim().split(/\s+/)[0] ?? "";
const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Card "o que vem além do salário": ícone, título, valor de 22px e texto ou itens. */
const CardValor = ({
  icone: Icone,
  titulo,
  valor,
  texto,
  itens,
}: {
  icone: LucideIcon;
  titulo: ReactNode;
  valor: number;
  texto?: ReactNode;
  itens?: { chave: string; rotulo: ReactNode; valor: number }[];
}) => (
  <section className="flex flex-col gap-3 rounded-xl border bg-card px-5 py-[18px]">
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-primary/10 text-primary">
        <Icone className="h-[18px] w-[18px]" />
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[15px] font-semibold">{titulo}</span>
        <span className="text-[22px] font-semibold tracking-[-0.01em] tabular-nums">{formatCents(valor)}</span>
      </div>
    </div>
    {texto && <p className="text-[13px] leading-relaxed text-muted-foreground">{texto}</p>}
    {itens && itens.length > 0 && (
      <div className="flex flex-col">
        {itens.map((i) => (
          <div key={i.chave} className="flex justify-between gap-3 border-t border-border-soft py-[7px] text-[13px]">
            <span className="text-muted-foreground">{i.rotulo}</span>
            <span className="tabular-nums">{formatCents(i.valor)}</span>
          </div>
        ))}
      </div>
    )}
  </section>
);

const ColaboradorDashboard = () => {
  const { user } = useAuth();
  const nomeBonificacao = useNomeBonificacao();
  const clt = user?.vinculo === "clt";
  const [dados, setDados] = useState<Remuneracao | null>(null);
  const [nfs, setNfs] = useState<ResumoNFs | null>(null);
  // Card de ação do mês: PJ = valor a emitir; CLT = último holerite.
  const [instrucao, setInstrucao] = useState<Instrucoes["instrucao"]>(null);
  const [ultimoHolerite, setUltimoHolerite] = useState<HoleriteHistorico | null>(null);

  useEffect(() => {
    api
      .get<Remuneracao>("/dashboard/colaborador/remuneracao")
      .then(setDados)
      .catch((err) => toast.error(err instanceof Error ? err.message : "Erro ao carregar seus valores"));
  }, []);

  useEffect(() => {
    if (!user) return;
    // Secundários: sem eles o card de ação mostra o texto genérico.
    if (clt) {
      api
        .get<HoleriteHistorico[]>("/folhas/me/holerites")
        .then((h) => setUltimoHolerite(h.find((x) => x.tipo === "mensal") ?? h[0] ?? null))
        .catch(() => {});
    } else {
      api
        .get<Instrucoes>("/instrucoes/me")
        .then((r) => setInstrucao(r?.instrucao ?? null))
        .catch(() => {});
    }
  }, [user, clt]);

  const carregarNfs = useCallback(async () => {
    if (clt) return;
    try {
      setNfs(await api.get<ResumoNFs>("/dashboard/colaborador"));
    } catch {
      // as NFs são secundárias aqui; o resto da página continua
    }
  }, [clt]);

  useEffect(() => {
    void carregarNfs();
  }, [carregarNfs]);
  useAtualizacaoNF(carregarNfs, (nfs?.recentes ?? []).some((n) => n.status === STATUS_AGUARDANDO_OCR));

  const partes: { chave: Chave; rotulo: string; cor: string }[] = [
    { chave: "salario", rotulo: clt ? "Salário" : "Salário (fixo + complemento)", cor: "var(--viz-1)" },
    { chave: "variaveis", rotulo: clt ? "Horas, adicionais, férias e 13º" : "Comissões e ajustes", cor: "var(--viz-2)" },
    { chave: "beneficios", rotulo: "Benefícios no Flash", cor: "var(--viz-3)" },
    { chave: "bonificacao", rotulo: nomeBonificacao, cor: "var(--viz-4)" },
    { chave: "saude", rotulo: "Plano de saúde coberto", cor: "var(--viz-5)" },
    { chave: "fgts", rotulo: "FGTS depositado", cor: "var(--viz-6)" },
  ];
  const visiveis = dados ? partes.filter((p) => dados.totais[p.chave] > 0) : [];
  const t = dados?.totais;
  const extra = t && t.salario > 0 ? t.alemDoSalario / t.salario : null;

  // Texto do card de ação do mês, com os dados reais quando existem.
  const acao = clt
    ? {
        rotulo: ultimoHolerite ? capitalizar(formatMesRef(ultimoHolerite.competencia)) : "Holerite",
        texto: ultimoHolerite
          ? `Seu holerite de ${formatMesRef(ultimoHolerite.competencia).split(" ")[0]} já está disponível.`
          : "Seu holerite aparece aqui quando o RH fecha a folha do mês.",
      }
    : {
        rotulo: instrucao ? `Competência ${formatMesRef(instrucao.competencia)}` : "Nota fiscal do mês",
        texto: !instrucao
          ? "O valor da NF aparece aqui quando o RH fecha a folha do mês."
          : instrucao.nfStatus
            ? `Você já enviou a NF de ${formatCents(instrucao.valor)} para ${instrucao.tomadorNome} (${instrucao.nfStatus.toLowerCase()}).`
            : `Emitir NF para ${instrucao.tomadorNome} no valor de ${formatCents(instrucao.valor)}.`,
      };

  return (
    <RoleGuard role={["colaborador", "rh", "financeiro"]}>
      <AppLayout title="Dashboard">
        <div className="mx-auto flex max-w-6xl flex-col gap-5 tabular-nums">
          {/* Abertura: o número que importa + a ação do mês */}
          <section
            className="flex flex-wrap items-end justify-between gap-6 rounded-[14px] px-6 py-7 text-white sm:px-8"
            style={{ background: "var(--gradient-dark)" }}
          >
            <div className="flex min-w-0 flex-[1_1_420px] flex-col gap-2">
              <span className="text-sm opacity-80">Olá, {primeiroNome(user?.name)}</span>
              {t && t.total > 0 ? (
                <>
                  <span className="text-[17px] font-medium opacity-90">Nos últimos 12 meses, você recebeu com a gente</span>
                  <span className="text-[38px] font-semibold leading-[1.05] tracking-heroi sm:text-[46px]">
                    {formatCents(t.total)}
                  </span>
                  {t.alemDoSalario > 0 && (
                    <span className="max-w-[560px] text-sm leading-normal opacity-90">
                      Além do seu salário, foram <strong>{formatCents(t.alemDoSalario)}</strong>
                      {extra !== null && <> — {porcento(extra, 0)} a mais</>} em benefícios, reconhecimento e cuidado com
                      você.
                    </span>
                  )}
                </>
              ) : (
                <span className="text-2xl font-semibold">Que bom ter você aqui</span>
              )}
            </div>
            <div className="flex flex-[0_1_320px] flex-col gap-3 rounded-xl border border-white/[0.12] bg-white/[0.07] px-[18px] py-4">
              <span className="text-xs uppercase tracking-[0.08em] opacity-70">{acao.rotulo}</span>
              <span className="text-[15px] leading-snug">{acao.texto}</span>
              <Button asChild className="h-[42px] shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.5)]">
                {clt ? (
                  <Link to={ultimoHolerite ? `/colaborador/demonstrativo/${ultimoHolerite.competencia}` : "/colaborador/demonstrativo"}>
                    <ReceiptText /> Ver meu holerite
                  </Link>
                ) : (
                  <Link to="/colaborador/nova">
                    <FilePlus2 /> Enviar nova NF
                  </Link>
                )}
              </Button>
            </div>
          </section>

          {dados && t && t.total > 0 ? (
            <>
              {/* Para onde foi: parte do todo */}
              <SecaoCard
                titulo="Tudo o que compõe o que você recebe"
                descricao={`${capitalizar(formatMesRef(dados.periodo.de))} a ${formatMesRef(dados.periodo.ate)} · folhas fechadas e NFs aprovadas`}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex h-3.5 w-full gap-[2px] overflow-hidden rounded-full" role="img" aria-label="Composição do total recebido">
                    {visiveis.map((p) => (
                      <div
                        key={p.chave}
                        style={{ width: `${(dados.totais[p.chave] / t.total) * 100}%`, background: p.cor }}
                        title={`${p.rotulo}: ${formatCents(dados.totais[p.chave])}`}
                      />
                    ))}
                  </div>
                  <ul className="grid gap-x-6 gap-y-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
                    {visiveis.map((p) => (
                      <li key={p.chave} className="flex items-start gap-2">
                        <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: p.cor }} aria-hidden />
                        <span className="flex min-w-0 flex-col gap-0.5">
                          <span className="text-[13px] text-muted-foreground">{p.rotulo}</span>
                          <span className="text-[15px] font-semibold">
                            {formatCents(dados.totais[p.chave])}{" "}
                            <span className="text-xs font-normal text-muted-foreground">
                              {porcento(dados.totais[p.chave] / t.total, 0)}
                            </span>
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </SecaoCard>

              {/* O que vem além do salário: Flash, saúde, bonificação e comissões (PJ) ou FGTS (CLT) */}
              <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))" }}>
                {t.beneficios > 0 && (
                  <CardValor
                    icone={Gift}
                    titulo="Seus benefícios no cartão Flash"
                    valor={t.beneficios}
                    itens={dados.beneficios.map((b) => ({
                      chave: b.nome,
                      rotulo: `${b.nome} · ${b.meses} ${b.meses === 1 ? "mês" : "meses"}`,
                      valor: b.total,
                    }))}
                  />
                )}

                {t.saude > 0 && (
                  <CardValor
                    icone={HeartPulse}
                    titulo="Cuidamos da sua saúde"
                    valor={t.saude}
                    texto={
                      <>
                        É o quanto da sua coparticipação no plano de saúde a empresa pagou por você
                        {dados.saude.mesesComUso > 0 && (
                          <> em {dados.saude.mesesComUso} {dados.saude.mesesComUso === 1 ? "mês" : "meses"}</>
                        )}
                        . Todo mês, até {formatCents(dados.saude.franquiaMensal)} do seu uso fica por nossa conta.
                      </>
                    }
                  />
                )}

                {t.bonificacao > 0 && (
                  <CardValor
                    icone={Trophy}
                    titulo={nomeBonificacao}
                    valor={t.bonificacao}
                    texto="O reconhecimento pelas metas que você ajudou a bater."
                    itens={dados.bonificacoes.map((b) => ({
                      chave: b.competencia,
                      rotulo: `${capitalizar(formatMesRef(b.competencia))}${
                        b.adiantamento ? " · adiantamento" : b.numeroParcelas > 1 ? ` · parcela ${b.parcela}/${b.numeroParcelas}` : ""
                      }`,
                      valor: b.valor,
                    }))}
                  />
                )}

                {clt
                  ? t.fgts > 0 && (
                      <CardValor
                        icone={Landmark}
                        titulo="FGTS depositado para você"
                        valor={t.fgts}
                        texto="8% do que você recebe, depositados todo mês na sua conta do FGTS, além do seu salário."
                      />
                    )
                  : t.variaveis > 0 && (
                      <CardValor
                        icone={TrendingUp}
                        titulo="Comissões e ajustes"
                        valor={t.variaveis}
                        texto="O que você recebeu além do fixo, pelas suas vendas e resultados."
                      />
                    )}
              </div>

              <SecaoCard titulo="Mês a mês">
                <GraficoColunas
                  dados={dados.mensal}
                  chaveX="competencia"
                  rotuloX="Mês"
                  formatarX={mesCurto}
                  formatarValor={formatCents}
                  formatarEixo={(v) => formatCents(v).replace(/,\d{2}$/, "")}
                  mostrarTotal
                  series={visiveis.map((p) => ({ chave: p.chave, rotulo: p.rotulo, cor: p.cor }))}
                />
              </SecaoCard>
            </>
          ) : (
            dados && (
              <section className="rounded-xl border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
                Assim que sua primeira folha for fechada, você vai ver aqui tudo o que recebe com a gente: salário,
                benefícios, plano de saúde e muito mais.
              </section>
            )
          )}

          {!clt && nfs && (
            <section className="overflow-hidden rounded-xl border bg-card">
              <div className="flex items-center justify-between gap-3 px-5 py-[18px]">
                <div className="flex flex-col gap-1">
                  <h3 className="text-base font-semibold">Suas notas fiscais</h3>
                  <p className="text-xs text-muted-foreground">
                    {nfs.emAnalise} em análise · {nfs.aprovadas} aprovadas · {nfs.recusadas} recusadas
                  </p>
                </div>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/colaborador/notas">
                    Ver todas <ArrowRight />
                  </Link>
                </Button>
              </div>
              {nfs.recentes.length === 0 ? (
                <div className="border-t border-border-soft px-5 py-8 text-center text-sm text-muted-foreground">
                  Você ainda não enviou nenhuma NF.
                </div>
              ) : (
                nfs.recentes.slice(0, 3).map((n) => (
                  <Link
                    key={n.id}
                    to={`/colaborador/notas/${n.id}`}
                    className="flex items-center justify-between gap-3 border-t border-border-soft px-5 py-3 transition-colors hover:bg-row-hover"
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate text-sm font-medium">
                        NF {n.numero} · {n.tomadorNome ?? "Sem tomador"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {capitalizar(formatMesRef(n.mesReferencia))} · enviada em {formatDate(n.dataEnvio)}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="hidden text-sm font-semibold sm:inline">{formatBRL(n.valor)}</span>
                      <StatusBadge status={n.status} />
                    </div>
                  </Link>
                ))
              )}
            </section>
          )}
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default ColaboradorDashboard;
